import React, { useEffect, useMemo, useState } from "react";
import { TextField, Typography } from "@mui/material";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { Icon } from "@progress/kendo-react-common";
import { Btn } from "../../AbstractElements";
import axios from "axios";
import { ShowError, ShowSucess, getUserRole } from "../../_helper/helper";
import { LoadingPanel } from "../../CommonComponents/CommonFunction";
import { getFormalApiErrorMessage } from "../../utils/apiError";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "oidc-react";

const ALLOWED_ROLES = ["SUPER-USER", "Classification"];

const JobPositionRequirementById = () => {
    const history = useNavigate();
    const { positionNo: routePositionNo } = useParams();
    const { userData } = useAuth();
    const currentRole = getUserRole(userData);
    const canEdit = ALLOWED_ROLES.includes(currentRole);
    const [requirements, setRequirements] = useState({});
    const [initialRequirements, setInitialRequirements] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [updating, setUpdating] = useState(false);

    const positionNo = useMemo(() => {
        if (routePositionNo) return routePositionNo;
        const parts = window.location.pathname.split("/").filter(Boolean);
        return parts[parts.length - 1] || "";
    }, [routePositionNo]);

    const emptyRequirements = {
        QualificationRequirement: "",
        NatureofWork: "",
        illustrativeExofWork: "",
        KnowledgeAbilitiesSkills: "",
        MinimumEducationReq: "",
        DocRequirement: "",
        SuitabilityDeterminationForm: "",
        ProhibitionPursuant: "",
        ExaminationReq: "",
        InterviewProcedures: "",
        EmploymentMedicalExamination: "",
        WorkEligibility: "",
        DrugScreening: "",
        PoliceCourtClearances: "",
        WhereToApply: "",
        ForMoreInfo: "",
        NecessarySpecialQualifications: "",
    };

    const hasRequirementChanges = useMemo(() => {
        if (!isEditing) return false;
        return JSON.stringify(requirements) !== JSON.stringify(initialRequirements);
    }, [isEditing, requirements, initialRequirements]);

    const generateAnnouncementData = async () => {
        if (!positionNo) {
            ShowError("Position number not found in URL.");
            return false;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_DOTNET_URL}/JobApplicantAdvanceSearch/JobApplicant-global?pageNumber=1&pageSize=1`,
                {
                    tableName: "JA_Registry",
                    searchParams: "[]",
                    columns: "*",
                    pageNumber: 1,
                    pageSize: 1,
                    sortData: "",
                    additionalWhere: `PositionNo = '${positionNo}'`,
                    qID: 0,
                    location: "global",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("jwt_access_token")}`,
                    },
                }
            );

            if (response?.data?.results?.length) {
                const localReqs = response.data.results[response.data.results.length - 1];
                setRequirements({
                    QualificationRequirement: localReqs?.QualificationRequirement?.trim() || "",
                    NatureofWork: localReqs?.NatureofWork?.trim() || "",
                    illustrativeExofWork: localReqs?.illustrativeExofWork?.trim() || "",
                    KnowledgeAbilitiesSkills: localReqs?.KnowledgeAbilitiesSkills?.trim() || "",
                    MinimumEducationReq: localReqs?.MinimumEducationReq?.trim() || "",
                    DocRequirement: localReqs?.DocRequirement?.trim() || "",
                    SuitabilityDeterminationForm: localReqs?.SuitabilityDeterminationForm?.trim() || "",
                    ProhibitionPursuant: localReqs?.ProhibitionPursuant?.trim() || "",
                    ExaminationReq: localReqs?.ExaminationReq?.trim() || "",
                    InterviewProcedures: localReqs?.InterviewProcedures?.trim() || "",
                    EmploymentMedicalExamination: localReqs?.EmploymentMedicalExamination?.trim() || "",
                    WorkEligibility: localReqs?.WorkEligibility?.trim() || "",
                    DrugScreening: localReqs?.DrugScreening?.trim() || "",
                    PoliceCourtClearances: localReqs?.PoliceCourtClearances?.trim() || "",
                    WhereToApply: localReqs?.WhereToApply?.trim() || "",
                    ForMoreInfo: localReqs?.ForMoreInfo?.trim() || "",
                    NecessarySpecialQualifications:
                        localReqs?.NecessarySpecialQualifications?.trim() || "",
                });
                setInitialRequirements({
                    QualificationRequirement: localReqs?.QualificationRequirement?.trim() || "",
                    NatureofWork: localReqs?.NatureofWork?.trim() || "",
                    illustrativeExofWork: localReqs?.illustrativeExofWork?.trim() || "",
                    KnowledgeAbilitiesSkills: localReqs?.KnowledgeAbilitiesSkills?.trim() || "",
                    MinimumEducationReq: localReqs?.MinimumEducationReq?.trim() || "",
                    DocRequirement: localReqs?.DocRequirement?.trim() || "",
                    SuitabilityDeterminationForm: localReqs?.SuitabilityDeterminationForm?.trim() || "",
                    ProhibitionPursuant: localReqs?.ProhibitionPursuant?.trim() || "",
                    ExaminationReq: localReqs?.ExaminationReq?.trim() || "",
                    InterviewProcedures: localReqs?.InterviewProcedures?.trim() || "",
                    EmploymentMedicalExamination: localReqs?.EmploymentMedicalExamination?.trim() || "",
                    WorkEligibility: localReqs?.WorkEligibility?.trim() || "",
                    DrugScreening: localReqs?.DrugScreening?.trim() || "",
                    PoliceCourtClearances: localReqs?.PoliceCourtClearances?.trim() || "",
                    WhereToApply: localReqs?.WhereToApply?.trim() || "",
                    ForMoreInfo: localReqs?.ForMoreInfo?.trim() || "",
                    NecessarySpecialQualifications:
                        localReqs?.NecessarySpecialQualifications?.trim() || "",
                });
                return true;
            } else {
                setRequirements(emptyRequirements);
                setInitialRequirements(emptyRequirements);
                return false;
            }
        } catch (error) {
            setRequirements(emptyRequirements);
            setInitialRequirements(emptyRequirements);
            ShowError(getFormalApiErrorMessage(error, undefined, { suppressNotFound: true }));
            console.warn("Cannot retrieve announcement data", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const updateRequirementData = async () => {
        if (!positionNo) {
            ShowError("Position number not found in URL.");
            return false;
        }

        const isConfirmed = window.confirm(
            "Are you sure you want to update requirements?"
        );
        if (!isConfirmed) {
            return false;
        }

        setUpdating(true);
        try {
            const payload = {
                positionNo,
                qualificationRequirement: requirements.QualificationRequirement,
                natureofWork: requirements.NatureofWork,
                illustrativeExofWork: requirements.illustrativeExofWork,
                knowledgeAbilitiesSkills: requirements.KnowledgeAbilitiesSkills,
                minimumEducationReq: requirements.MinimumEducationReq,
                docRequirement: requirements.DocRequirement,
                suitabilityDeterminationForm: requirements.SuitabilityDeterminationForm,
                prohibitionPursuant: requirements.ProhibitionPursuant,
                examinationReq: requirements.ExaminationReq,
                interviewProcedures: requirements.InterviewProcedures,
                employmentMedicalExamination: requirements.EmploymentMedicalExamination,
                workEligibility: requirements.WorkEligibility,
                drugScreening: requirements.DrugScreening,
                policeCourtClearances: requirements.PoliceCourtClearances,
                whereToApply: requirements.WhereToApply,
                forMoreInfo: requirements.ForMoreInfo,
                necessarySpecialQualifications: requirements.NecessarySpecialQualifications,
            };

            await axios.put(`${process.env.REACT_APP_API_DOTNET_URL}/JARegistry/Ja_Registries_update`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sessionStorage.getItem("jwt_access_token")}`,
                },
            });

            ShowSucess("Requirements updated successfully.");
            setIsEditing(false);
            await generateAnnouncementData();
            return true;
        } catch (error) {
            ShowError(getFormalApiErrorMessage(error));
            console.warn("Cannot update requirements", error);
            return false;
        } finally {
            setUpdating(false);
        }
    };

    useEffect(() => {
        generateAnnouncementData();
    }, [positionNo]);

    const renderTextArea = (key, label, required = false, disabled = false) => (
        <TextField
            fullWidth
            multiline
            minRows={4}
            margin="normal"
            label={label}
            required={required}
            disabled={disabled}
            value={requirements?.[key] || ""}
            onChange={(e) =>
                setRequirements((prev) => ({ ...prev, [key]: e.target.value }))
            }
        />
    );

    return (
        <Container fluid>
            {(loading || updating) && <LoadingPanel />}
            <Row className="bg-primary rounded py-3 mb-3 align-items-center">
                <Col md={6}>
                    <div style={{ display: "flex", alignItems: "center" }}>

                        <Btn
                            attrBtn={{
                                color: "Primary",
                                outline: true,
                                onClick: () => history("/jobPositionRequirement"),
                                style: { marginLeft: "8px", color: "white", borderColor: "white" },
                            }}
                        >
                            <Icon style={{ fontSize: "18px" }} name="arrow-left" /> &nbsp;Back
                        </Btn>
                        <Typography variant="h5">
                            <Icon style={{ fontSize: "25px", margin: "10px" }} name="list-latin-small" />
                            Requirement
                        </Typography>
                    </div>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                    {canEdit && (
                        <Btn
                            attrBtn={{
                                color: "Primary",
                                outline: true,
                                onClick: async () => {
                                    if (!isEditing) {
                                        setIsEditing(true);
                                        return;
                                    }
                                    await updateRequirementData();
                                },
                                disabled: loading || updating || (isEditing && !hasRequirementChanges),
                                style: { marginRight: "10px", color: "white", borderColor: "white" },
                            }}
                        >
                            <Icon
                                style={{ fontSize: "18px" }}
                                name={isEditing ? "save" : "border-color"}
                            />{" "}
                            &nbsp;{updating ? "Updating..." : isEditing ? "Update" : "Edit"}
                        </Btn>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col md={12}>
                    <Typography variant="subtitle1">Position No: {positionNo || "-"}</Typography>
                </Col>
            </Row>
            <Row>
                <Col sm="12">
                    <Card>
                        <CardBody>
                            <Row>
                                <Col md={12}>
                                    {renderTextArea(
                                        "QualificationRequirement",
                                        "Qualification Requirements",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "NecessarySpecialQualifications",
                                        "Necessary Special Qualification",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea("NatureofWork", "Nature of Work", true, !isEditing)}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "illustrativeExofWork",
                                        "Illustrative Example of Work",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "KnowledgeAbilitiesSkills",
                                        "Knowledge , Abilities & Skills",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "MinimumEducationReq",
                                        "Minimum Educational Requirements",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "DocRequirement",
                                        "Documentation Requirements",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "SuitabilityDeterminationForm",
                                        "Suitability Determination Form",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "ProhibitionPursuant",
                                        "Prohibition Pursuant to P.L.28-98",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "InterviewProcedures",
                                        "Interviewing Procedures",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "ExaminationReq",
                                        "Examination Requirements",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "EmploymentMedicalExamination",
                                        "Employment Medical Examination",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea("WorkEligibility", "Work Eligibility", true, !isEditing)}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea("DrugScreening", "Drug Screening", true, !isEditing)}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea(
                                        "PoliceCourtClearances",
                                        "Police & Court Clearances Requirements",
                                        true,
                                        !isEditing
                                    )}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea("WhereToApply", "Where to Apply", true, !isEditing)}
                                </Col>
                                <Col md={12}>
                                    {renderTextArea("ForMoreInfo", "For More Information", true, !isEditing)}
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default JobPositionRequirementById;
